/* eslint-disable */
import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  IconButton,
  Box,
  CircularProgress,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../services/api';

const ModalImageCors = ({ imageUrl }) => {
  const theme = useTheme();
  const [fetching, setFetching] = useState(true);
  const [blobUrl, setBlobUrl] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const isMounted = useRef(true);
  const blobUrlRef = useRef('');
  const imageRef = useRef(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const isSideways = rotation === 90 || rotation === 270;
  const offset = isSideways
    ? imageSize.width > imageSize.height
      ? (imageSize.width - imageSize.height) / 2.25
      : 0
    : 0;

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      if (blobUrlRef.current) {
        window.URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let currentBlobUrl = '';

    if (!imageUrl) {
      if (isMounted.current) {
        setFetching(false);
      }
      return;
    }

    const fetchImage = async () => {
      try {
        setFetching(true);
        const { data, headers } = await api.get(imageUrl, {
          responseType: 'blob',
        });

        if (isMounted.current) {
          const url = window.URL.createObjectURL(
            new Blob([data], { type: headers['content-type'] }),
          );
          currentBlobUrl = url;
          blobUrlRef.current = url;
          setBlobUrl(url);
          setFetching(false);
        }
      } catch (error) {
        console.error('Error fetching image:', error);
        if (isMounted.current) {
          setFetching(false);
        }
      }
    };

    fetchImage();

    return () => {
      if (currentBlobUrl) {
        window.URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [imageUrl]);

  const handleRotateLeft = () => {
    setRotation((prev) => (prev - 90 > 0 ? prev - 90 : 0));
  };

  const handleRotateRight = () => {
    setRotation((prev) => (prev + 90 < 361 ? prev + 90 : 360));
  };

  const handleClose = () => {
    setModalOpen(false);
    setRotation(0);
  };

  const handleDownload = () => {
    const url = blobUrl || imageUrl;
    if (!url) return;

    const filename = imageUrl.split('/').pop().split('?')[0] || 'imagem';

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!imageUrl) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          lineHeight: 0,
        }}
      >
        <img
          src={fetching ? imageUrl : blobUrl}
          alt='contact'
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
          onClick={() => setModalOpen(true)}
        />
      </Box>

      <Dialog
        open={modalOpen}
        onClose={handleClose}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(25, 39, 52, 0.9)',
            boxShadow: 'none',
            margin: 0,
            height: '100vh',
            maxWidth: '100vw',
            borderRadius: 2,
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            ...theme.custom.scrollbarStyles,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '8px 16px',
            backgroundColor: 'rgba(25, 39, 52, 0.8)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            zIndex: 2,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={handleDownload}
            disabled={fetching}
            sx={{ color: 'white', mr: 1 }}
            size='small'
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={handleRotateLeft}
            sx={{ color: 'white', mr: 1 }}
            size='small'
          >
            <RotateLeftIcon />
          </IconButton>
          <IconButton
            onClick={handleRotateRight}
            sx={{ color: 'white', mr: 1 }}
            size='small'
          >
            <RotateRightIcon />
          </IconButton>

          <IconButton
            onClick={handleClose}
            sx={{ color: 'white' }}
            size='small'
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 2,
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0, 0, 0, 0.1)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255, 255, 255, 0.5)',
            },
          }}
          onClick={handleClose}
        >
          {fetching ? (
            <CircularProgress sx={{ color: 'white' }} />
          ) : (
            <Box
              sx={{
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.3s ease',
                display: 'flex',
                position: 'relative',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: 'fit-content',
                minHeight: 'fit-content',
                top: isSideways ? `${offset}px` : 0,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                ref={imageRef}
                src={blobUrl}
                alt='full size'
                onLoad={(e) =>
                  setImageSize({
                    width: e.target.naturalWidth,
                    height: e.target.naturalHeight,
                  })
                }
                style={{
                  maxHeight: '91vh',
                  objectFit: 'contain',
                  borderRadius: 5,
                }}
              />
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default ModalImageCors;
